/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: Septiembre 2026
 * Licencia: Propiedad Privada
 */
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../../../UserContext";
import { esAdministrador, rutaCambioContrasena } from "../../../utils/perfil";
import api from "../../../axiosConfig";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Alert,
  Typography,
  useMediaQuery,
  CircularProgress,
  Fade,
  Paper,
  keyframes,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import {
  Lock,
  Visibility,
  VisibilityOff,
  Warning,
  CheckCircle,
} from "@mui/icons-material";
import logo from "../../../Assets/img/logo.webp";

// Animaciones
const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// Contenedor principal con fondo degradado
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  background: "linear-gradient(135deg, #0a2e5c 0%, #051b38 100%)",
  position: "relative",
  overflow: "hidden",
  padding: theme.spacing(2),
  backgroundImage: `
    radial-gradient(circle at 20% 80%, rgba(10, 46, 92, 0.4) 0%, transparent 60%),
    radial-gradient(circle at 80% 20%, rgba(10, 46, 92, 0.25) 0%, transparent 60%),
    radial-gradient(circle at 60% 60%, rgba(10, 46, 92, 0.18) 0%, transparent 50%)
  `,
}));

// Logo con efectos
const StyledLogo = styled("img")(({ theme, isMobile }) => ({
  width: isMobile ? 180 : 240,
  marginBottom: theme.spacing(3),
  filter: "drop-shadow(0 0 20px rgba(10, 46, 92, 0.8))",
  animation: `${floatAnimation} 6s ease-in-out infinite`,
}));

// Contenedor del formulario con glass effect
const FormContainer = styled(Paper)(({ theme, isMobile }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(20px)",
  border: "2px solid rgba(255, 255, 255, 0.3)",
  padding: isMobile ? theme.spacing(3) : theme.spacing(4),
  borderRadius: 20,
  boxShadow: `
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.8)
  `,
  maxWidth: isMobile ? "90vw" : 420,
  width: "100%",
}));

// Input personalizado
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),

  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: 12,
    transition: "all 0.3s ease",

    "& fieldset": {
      borderColor: "rgba(10, 46, 92, 0.3)",
      borderWidth: 2,
    },

    "&:hover fieldset": {
      borderColor: "rgba(10, 46, 92, 0.5)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#0a2e5c",
    },
  },

  "& .MuiInputLabel-root": {
    color: "#2c3e50",
    fontWeight: 600,

    "&.Mui-focused": {
      color: "#0a2e5c",
    },
  },
}));

// Botones
const SubmitButton = styled(Button)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  borderRadius: 12,
  background: "linear-gradient(135deg, #0a2e5c, #051b38)",
  color: "white",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 1,
  boxShadow: "0 8px 20px rgba(10, 46, 92, 0.6)",
  transition: "all 0.3s ease",

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 25px rgba(10, 46, 92, 0.8)",
    background: "linear-gradient(135deg, #051b38, #082340)",
  },

  "&:disabled": {
    background: "rgba(10, 46, 92, 0.5)",
    transform: "none",
  },
}));

function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Verificar si es modo obligatorio
  const searchParams = new URLSearchParams(location.search);
  const isMandatory = searchParams.get("mandatory") === "true";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    // Validaciones
    if (newPassword.length < 8) {
      setErrorMessage("La nueva contraseña debe tener al menos 8 caracteres");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Las contraseñas no coinciden");
      return;
    }

    if (currentPassword === newPassword) {
      setErrorMessage("La nueva contraseña debe ser diferente a la actual");
      return;
    }

    setLoading(true);

    try {
      // Una sola contraseña por persona: la ruta del administrador cambia también la de conductor
      await api.post(rutaCambioContrasena(user), {
        currentPassword,
        newPassword,
      });

      setSuccessMessage("Contraseña actualizada correctamente");

      // Actualizar el usuario para quitar el flag
      if (user) {
        const updatedUser = { ...user, requiere_cambio_password: false };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      // Esperar un momento para mostrar el mensaje de exito
      setTimeout(() => {
        navigate(esAdministrador(user) ? "/modules/Dashboard" : "/modules/PreOperationalForm");
      }, 1500);
    } catch (error) {
      console.error("Error al cambiar contraseña:", error);

      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage("Error al cambiar la contraseña. Intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <StyledLogo src={logo} alt="Mogotax Logo" isMobile={isMobile} />

      <FormContainer elevation={0} isMobile={isMobile}>
        {/* Titulo */}
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            color: "#0a2e5c",
            fontWeight: 700,
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          {isMandatory ? (
            <>
              <Warning sx={{ color: "#ff9800" }} />
              Cambio de Contraseña Requerido
            </>
          ) : (
            <>
              <Lock />
              Cambiar Contraseña
            </>
          )}
        </Typography>

        {/* Mensaje informativo para modo obligatorio */}
        {isMandatory && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              borderRadius: 2,
              backgroundColor: "rgba(255, 152, 0, 0.1)",
              border: "1px solid rgba(255, 152, 0, 0.3)",
            }}
          >
            Tu contraseña ha sido reiniciada por un operador. Debes crear una
            nueva contraseña para continuar.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Contraseña actual/temporal */}
          <StyledTextField
            fullWidth
            label={isMandatory ? "Contraseña temporal" : "Contraseña actual"}
            type={showCurrentPassword ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "#0a2e5c" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                    size="small"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Nueva contraseña */}
          <StyledTextField
            fullWidth
            label="Nueva contraseña"
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            helperText="Minimo 8 caracteres"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "#0a2e5c" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    size="small"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Confirmar contraseña */}
          <StyledTextField
            fullWidth
            label="Confirmar nueva contraseña"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "#0a2e5c" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Mensajes de error */}
          {errorMessage && (
            <Fade in={Boolean(errorMessage)}>
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: "rgba(244, 67, 54, 0.1)",
                  border: "1px solid rgba(244, 67, 54, 0.3)",
                }}
              >
                {errorMessage}
              </Alert>
            </Fade>
          )}

          {/* Mensaje de exito */}
          {successMessage && (
            <Fade in={Boolean(successMessage)}>
              <Alert
                severity="success"
                icon={<CheckCircle />}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                  border: "1px solid rgba(76, 175, 80, 0.3)",
                }}
              >
                {successMessage}
              </Alert>
            </Fade>
          )}

          {/* Boton de enviar */}
          <SubmitButton type="submit" variant="contained" disabled={loading}>
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Actualizando...
              </Box>
            ) : (
              "Cambiar Contraseña"
            )}
          </SubmitButton>
        </Box>
      </FormContainer>
    </PageContainer>
  );
}

export default ChangePasswordPage;
